import type {
  GameContext,
  GameInstance,
  GameModule,
  PreloadContext,
} from '@arcraz/arcadia-types';
import { SectorSevenInstance } from './instance.js';
import { PALETTE_HINT } from './palette.js';

/**
 * SECTOR SEVEN — seventh cartridge, SECOND vector cartridge.
 *
 * 960×540 vector. Tempest (1981) + Gyruss (1983) heritage. Defender on
 * the rim of a polygonal tube; enemies climb out from the vanishing
 * point along radial lanes; player rotates around the rim and shoots
 * inward. Hex (odd levels) + octagon (even levels) geometries in MVP.
 *
 * Original mechanic: **dive into the tube** — fireB plunges the
 * player ~40% into the tube for 1 second. Rotation locked during the
 * dive, but bullets deal 3× damage. Risk/reward: commit to a lane,
 * punch through a Pulsar or Spiker stack.
 *
 * MVP scope (sc-3129):
 *   - hex (6 lanes) + octagon (8 lanes) tube geometries
 *   - 3 enemy AIs: Flipper (climb + edge flip), Spiker (climb + drop
 *     spikes), Pulsar (pulse invuln + lane-kill while pulsing)
 *   - bullets w/ 8-max cap, dive 3× damage
 *   - 3 lives, 1s respawn invuln
 *   - level progression by enemy kill count + level-clear bonus
 */
const module_: GameModule = {
  id: 'sector-seven',
  title: 'SECTOR SEVEN',
  manufacturer: 'ARCADIA AMUSEMENTS',
  year: 1984,
  genre: 'shooter',
  resolution: { w: 960, h: 540 },
  pixelated: false,
  paletteHint: PALETTE_HINT,
  validatorVersion: 1,
  controls: [
    { keys: 'LEFT / RIGHT', action: 'ROTATE LANE' },
    { keys: 'Z / SPACE', action: 'FIRE' },
    { keys: 'X', action: 'DIVE (3× DAMAGE)' },
    { keys: 'Z', action: 'RESTART (AT GAME OVER)' },
  ],

  async preload(ctx: PreloadContext): Promise<void> {
    // Rotate — short tick on lane snap. Frequent input so kept quiet.
    ctx.registerSfx('rotate', {
      wave: 'triangle',
      pitch: [{ t: 0, f: 660 }, { t: 15, f: 880 }],
      gain: [{ t: 0, g: 0 }, { t: 2, g: 0.08 }, { t: 15, g: 0.0 }],
    });
    // Shoot — bullet fire down a lane.
    ctx.registerSfx('shoot', {
      wave: 'square',
      pitch: [{ t: 0, f: 900 }, { t: 30, f: 600 }],
      gain: [{ t: 0, g: 0 }, { t: 3, g: 0.1 }, { t: 30, g: 0.0 }],
    });
    // Dive — descending whoosh on dive start.
    ctx.registerSfx('dive', {
      wave: 'sawtooth',
      pitch: [{ t: 0, f: 700 }, { t: 200, f: 200 }],
      gain: [{ t: 0, g: 0 }, { t: 8, g: 0.28 }, { t: 200, g: 0.0 }],
      fm: { ratio: 0.5, index: 30 },
    });
    // Enemy kill — quick popping confirmation.
    ctx.registerSfx('enemy-kill', {
      wave: 'noise',
      pitch: [{ t: 0, f: 320 }, { t: 90, f: 120 }],
      gain: [{ t: 0, g: 0 }, { t: 4, g: 0.22 }, { t: 90, g: 0.0 }],
    });
    // Spike laid — short metallic plink so the player hears spikes
    // dropping in lanes they can't see.
    ctx.registerSfx('spike-laid', {
      wave: 'triangle',
      pitch: [{ t: 0, f: 440 }, { t: 60, f: 330 }],
      gain: [{ t: 0, g: 0 }, { t: 3, g: 0.14 }, { t: 60, g: 0.0 }],
    });
    // Pulsar pulse — warning warble during the invuln window.
    ctx.registerSfx('pulsar-pulse', {
      wave: 'sine',
      pitch: [{ t: 0, f: 220 }, { t: 100, f: 440 }, { t: 200, f: 220 }],
      gain: [{ t: 0, g: 0 }, { t: 6, g: 0.18 }, { t: 200, g: 0.0 }],
      fm: { ratio: 0.5, index: 20 },
    });
    // Crash — heavy descending noise on death.
    ctx.registerSfx('crash', {
      wave: 'noise',
      pitch: [{ t: 0, f: 220 }, { t: 280, f: 60 }],
      gain: [{ t: 0, g: 0 }, { t: 8, g: 0.55 }, { t: 280, g: 0.0 }],
    });
    // Level cleared — ascending fanfare (cabinet-wide convention).
    ctx.registerSfx('level-up', {
      wave: 'square',
      pitch: [
        { t: 0, f: 440 },
        { t: 100, f: 660 },
        { t: 200, f: 880 },
      ],
      gain: [{ t: 0, g: 0 }, { t: 8, g: 0.3 }, { t: 200, g: 0.0 }],
    });
  },

  init(ctx: GameContext): GameInstance {
    return new SectorSevenInstance(ctx);
  },
};

export default module_;
